import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  Paper,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';

import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import InstancesTable from '../components/InstancesTable';
import apiService from '../services/api';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const InstancesPage = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const instancesTableRef = React.useRef();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const { isDarkMode, toggleTheme } = useAppTheme();
  const { user, signOut } = useAuth();

  // Function to fetch instances data
  const fetchInstances = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getASGInstances();
      setInstances(response.instances || []);
      setLastRefreshed(new Date());
      
      // Also refresh the ASG size
      if (instancesTableRef.current) {
        instancesTableRef.current.refreshAsgSize();
      }
    } catch (err) {
      console.error('Failed to fetch instances:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch instances on component mount
  useEffect(() => {
    fetchInstances();

    // Optional: Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchInstances();
    }, 300000); // Refresh every 5 minutes

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Format the last refreshed time
  const formattedLastRefreshed = lastRefreshed.toLocaleTimeString();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: 'background.default',
      color: 'text.primary',
      transition: 'background-color 0.3s, color 0.3s'
    }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <img
            src={isDarkMode ? "/images/vitens-dark2.png" : "/images/vitens-dark2.png"}
            alt="Company Logo"
            width="100"
            height="100"
            style={{ height: 100, marginRight: 16 }}
          />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
            VCM (Vitens COMSOL Manager)
          </Typography>
          <Typography variant="h7" component="div" sx={{ flexGrow: 1 }}>
          AWS Server Manager - Version 1.0.0
          </Typography>
          <IconButton
            color="inherit"
            onClick={fetchInstances}
            aria-label="refresh"
            title="Refresh data"
            sx={{ mr: 1 }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            aria-label="toggle theme"
            title={isDarkMode ? "Switch to light theme" : "Switch to dark theme"}
            sx={{ mr: 2 }}
          >
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          
          {/* User profile chip */}
          <Chip
            avatar={<Avatar><AccountCircleIcon /></Avatar>}
            label={user?.attributes?.email || user?.username || 'User'}
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            color="primary"
            variant="outlined"
            sx={{ 
              borderColor: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)', 
              color: isDarkMode ? 'white' : 'inherit',
              '& .MuiChip-avatar': { 
                bgcolor: 'transparent', 
                color: isDarkMode ? 'white' : 'inherit' 
              },
              cursor: 'pointer'
            }}
          />
          
          {/* User menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            PaperProps={{
              elevation: 3,
              sx: { minWidth: 200 }
            }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">
                {user?.attributes?.given_name || user?.username || 'User'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={signOut}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h1">
              Auto Scaling Group Instances
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {formattedLastRefreshed}
            </Typography>
          </Box>

          <InstancesTable
            ref={instancesTableRef}
            instances={instances}
            loading={loading}
            error={error}
          />
        </Paper>
      </Container>

      <Box component="footer" sx={{ py: 2, bgcolor: 'background.paper', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Vitens - CAPTAIN COMSOL AWS Instance Manager
        </Typography>
      </Box>
    </Box>
  );
};

export default InstancesPage;
